import type {PosterProductDataForProcessDesign} from '@Panels/poster-publishing-options-panel/poster-publishing-options-panel.types';
import {Status} from '@Panels/poster-publishing-options-panel/poster-publishing-options-panel.types';
import {showErrorOnPanel} from '@Panels/poster-publishing-options-panel/poster-publishing-options-panel-library';
import type {
  AJAXPollingResponseForPreparingDesign,
  ShareToAppCheckoutResponse,
} from '@Panels/poster-publishing-options-panel/components/publishing-share-to-app-panel/publishing-share-to-app-panel.types';

export const SHARE_TO_APP_UNDEFINED = 'undefined';
export const ORDER_REGEN_RESPONSE = 1;
export const onWebShareError = (): void => {
  showErrorOnPanel(window.i18next.t('pmwjs_error_message'));
};

export const isNewPremiumDownload = (response: ShareToAppCheckoutResponse): boolean => {
  return (
    typeof response.status !== 'undefined' &&
    (response.status === Status.NEW_ORDER || response.status === Status.CREDIT_USABLE) &&
    typeof response.checkout !== 'undefined' &&
    response.checkout['raw-price'] === 0
  );
};
const prepareFileFromURLForShareToApp = (
  url: string,
  xhr: XMLHttpRequest,
  hasError: boolean,
  productUsed: PosterProductDataForProcessDesign,
  setFileToShare: (file: File) => void,
  setIsLoading: (isLoading: boolean) => void
): void => {
  xhr.open('GET', url, true);
  xhr.responseType = 'blob';
  xhr.onload = function onLoad(): void {
    if (!hasError) {
      const blob: Blob = xhr.response as Blob;
      const fileName = `${productUsed?.posterName ?? ''}.${productUsed?.format ?? ''}`;
      const file = new File([blob], fileName, {
        type: productUsed?.mimeType,
      });
      setFileToShare(file);
      setIsLoading(false);
    }
  };
};

export const sendXMLRequestForShareToApp = (
  url: string,
  productUsed: PosterProductDataForProcessDesign,
  setFileToShare: (file: File) => void,
  setIsLoading: (isLoading: boolean) => void
): void => {
  const xhr: XMLHttpRequest = new XMLHttpRequest();
  let hasError = false;

  prepareFileFromURLForShareToApp(url, xhr, hasError, productUsed, setFileToShare, setIsLoading);

  xhr.onreadystatechange = function onReadyStateChange(): void {
    if (xhr.readyState === 4) {
      if (xhr.status >= 400) {
        hasError = true;
        onWebShareError();
      }
    }
  };

  xhr.onerror = (): void => {
    onWebShareError();
    return;
  };

  window.PMW.addAjaxParamsForSubdomainToSubfolderRequests(xhr, true);
  xhr.send();
};

export const onShareToApp = (productUsed: PosterProductDataForProcessDesign, fileToShare: File, setIsLoading: (isLoading: boolean) => void): void => {
  let title = `PosterMyWall | ${productUsed?.posterName}`;
  let text = window.i18next.t('pmwjs_made_with_postermywall');

  /**
   * @see https://stackoverflow.com/questions/60302475/can-i-share-a-file-with-web-share-api-on-safari/66126240#66126240
   * Apparently, text/title needs to be empty for sharing assets through iOS
   */
  if (window.PMW.isIOS()) {
    title = '';
    text = '';
  }

  void navigator
    .share({
      files: [fileToShare],
      title,
      text,
    })
    .catch(() => {
      setIsLoading(false);
      // Not showing error here since simply closing the share dialog will also cause this
    });
};

export const onGenerationComplete = (
  data: AJAXPollingResponseForPreparingDesign,
  productUsed: PosterProductDataForProcessDesign,
  setFileToShare: (file: File) => void,
  setIsLoading: (isLoading: boolean) => void
): void => {
  if (data.url) {
    sendXMLRequestForShareToApp(data.url, productUsed, setFileToShare, setIsLoading);
  }
};

export const doWebShare = (
  productUsed: PosterProductDataForProcessDesign,
  posterHashedId: string,
  isHighRes: boolean,
  setCanShare: (canShare: boolean) => void,
  setIsLoading: (isLoading: boolean) => void,
  setFileToShare: (file: File) => void
): void => {
  setIsLoading(true);
  window.PMW.pollingAjaxCallAsync(doPrepareWebShareDesign.bind(null, posterHashedId, isHighRes), webShareDownloadReady.bind(null, 0, posterHashedId))
    .then((response: AJAXPollingResponseForPreparingDesign) => {
      setCanShare(true);
      onGenerationComplete(response, productUsed, setFileToShare, setIsLoading);
    })
    .catch(() => {
      onWebShareError();
    });
};
export const webShareDownloadReady = (isFreebie: number, posterHashedId: string): Promise<void> => {
  return window.PMW.readLocal('socialmedia/isWebShareDownloadReady', {
    hashedId: posterHashedId,
    isFreebie,
  });
};
export const doPrepareWebShareDesign = (posterHashedId: string, isHighRes: boolean): Promise<void> => {
  return window.PMW.writeLocal('socialmedia/prepareDesignForWebShare', {
    hashedId: posterHashedId,
    isHighres: isHighRes,
  });
};
